/**
 * Forward-migrating saved pages.
 *
 * Blocks get removed and props get renamed, and a page saved last week must still open. Puck
 * renders a "no config for this component" placeholder for an unknown block id, which loses
 * the copy inside it — so anything retired needs a conversion here, not just a deletion from
 * the registry.
 *
 * Every migration is a pure function over the saved tree. They run on load, and the result is
 * saved back the first time the page is edited.
 */

import { plainInline } from './shared/inline.js';
import { TextBlock } from './primitives/content.js';

interface SavedItem {
  type: string;
  props?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * HTML → the Text block's inline markup.
 *
 * Only the four things the old editor could produce: bold, italic, links and paragraph breaks.
 * Anything else becomes plain text, which is the right trade — losing a nested blockquote is
 * recoverable, losing the sentence is not.
 */
export function htmlToInline(html: string): string {
  if (typeof html !== 'string' || !html) return '';
  return (
    html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
      .replace(/<(strong|b)\b[^>]*>([\s\S]*?)<\/\1>/gi, '**$2**')
      .replace(/<(em|i)\b[^>]*>([\s\S]*?)<\/\1>/gi, '*$2*')
      .replace(/<a\b[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, '[$2]($1)')
      .replace(/<li\b[^>]*>([\s\S]*?)<\/li>/gi, '\n• $1')
      .replace(/<[^>]+>/g, '')
      // Entities the old editor emitted, in the order that avoids double-decoding `&amp;lt;`.
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
}

/** One item, migrated. Returns the same object when nothing applies. */
function migrateItem(item: SavedItem): SavedItem {
  if (item.type !== 'RichTextBlock') return item;

  const props = item.props ?? {};
  // The value was an HTML string, but a page saved while the `[object Object]` bug was live
  // could hold anything at all — so anything non-string contributes no copy rather than the
  // word "[object Object]".
  const raw = typeof props['html'] === 'string' ? props['html'] : '';

  return {
    ...item,
    type: 'TextBlock',
    props: {
      ...TextBlock.defaultProps,
      // `id` is Puck's, and reusing it keeps the block selected across the migration.
      ...(props['id'] ? { id: props['id'] } : {}),
      content: htmlToInline(raw),
      onDark: props['onDark'] === true,
    },
  };
}

/** Walk every item in a saved Puck tree, including slot contents. */
function walk(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(walk);
  if (!value || typeof value !== 'object') return value;

  const obj = value as Record<string, unknown>;
  const mapped: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(obj)) mapped[key] = walk(val);

  return typeof mapped['type'] === 'string' && 'props' in mapped
    ? migrateItem(mapped as SavedItem)
    : mapped;
}

/**
 * Migrate a saved Puck snapshot.
 *
 * Structurally typed rather than importing `PuckDataSnapshot`, so this stays usable on a raw
 * parsed file — an imported page has been through nobody's validation yet.
 */
export function migratePuckData<T>(data: T): T {
  return walk(data) as T;
}
